'use client';

import { InfoTooltip } from '@mokoboko/components';
import type { CSSProperties } from 'react';

import type { FactTile } from '@/lib/types';
import { useHuggedWidth } from '@/hooks/use-hugged-width';
import { mergeClassNames } from '@/lib/utils';
import { Illustration } from './illustration';

// Whole class names, so Tailwind finds them: one line of tiles from sm, and
// four as two rows of two, since four across squeeze each onto three lines.
const TILE_COLUMNS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2',
};

// Tiles two to a row have room from md for the icon beside a one-line text;
// three to a row would wrap it. A lone fact never reaches a tile from sm,
// since it joins the pills under the title (`LoneFactPill`). Compact rows share
// one box under a pack's or membership's count and price boxes on a phone,
// small enough that the boxes lead; a lone one is centred like them.
const TILE_LAYOUT = {
  rows: 'sm:flex-col sm:gap-2 sm:px-5 sm:py-5 sm:text-center',
  pairs:
    'sm:flex-col sm:gap-2 sm:px-6 sm:py-5 sm:text-center md:flex-row md:justify-center md:gap-3 md:py-2 md:text-left',
  compact: 'gap-2.5',
};

// Three across, a short text would sit on one line beside neighbours on two. A
// box narrower than the text's one line, by its own length so `ch` follows the
// font, makes two; `text-balance` then evens them. Three words or fewer stay on
// one line, since two would leave a word alone.
const twoLineWidth = (tile: FactTile) => {
  const text = [tile.lead, tile.prefix, tile.value, tile.tail].join('').trim();
  return text.split(/\s+/).length > 3
    ? `${Math.ceil(text.length * 0.6)}ch`
    : 'none';
};

// "30 / days" reads badly, so a number keeps to the word after it.
const keepNumberWithUnit = (value: string) =>
  value.replace(/(\d) /g, '$1\u00a0');

const FactTileText = ({
  tile,
  twoLines,
}: {
  tile: FactTile;
  twoLines: boolean;
}) => {
  const hug = useHuggedWidth();
  const value = tile.value && keepNumberWithUnit(tile.value);

  return (
    <span
      ref={hug}
      className={mergeClassNames(
        'text-balance',
        twoLines && 'sm:max-w-[var(--tile-text-width)]'
      )}
      style={
        twoLines
          ? ({ '--tile-text-width': twoLineWidth(tile) } as CSSProperties)
          : undefined
      }
    >
      {tile.lead}
      {tile.prefix}
      {value && (
        <span className="font-bold text-gray-900">
          {tile.tail ? value : <WithInfo text={value} tile={tile} />}
        </span>
      )}
      {tile.tail && <WithInfo text={tile.tail} tile={tile} />}
    </span>
  );
};

// The icon keeps to the last word, so it never starts a line alone, and is
// centred on it: the lib wraps the icon in a flex span, whose baseline is the
// icon's bottom.
const WithInfo = ({ text, tile }: { text: string; tile: FactTile }) => {
  if (!tile.info) return text;
  const cut = text.lastIndexOf(' ') + 1;

  return (
    <>
      {text.slice(0, cut)}
      <span className="inline-flex items-center">
        {text.slice(cut)}
        <FactTileInfo tile={tile} />
      </span>
    </>
  );
};

export const FactTileInfo = ({ tile }: { tile: FactTile }) =>
  tile.info ? (
    <InfoTooltip
      content={
        <span className="flex max-w-64 flex-col gap-1.5 text-left">
          {tile.info.lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </span>
      }
      ariaLabel={tile.info.label}
      className="ml-1"
    />
  ) : null;

export const FactTiles = ({
  tiles,
  compact = false,
  className,
}: {
  tiles: FactTile[];
  compact?: boolean;
  className?: string;
}) => {
  const layout = compact ? 'compact' : tiles.length === 3 ? 'rows' : 'pairs';

  return (
    <ul
      aria-label="Good to know"
      className={mergeClassNames(
        compact
          ? 'flex flex-col gap-2.5 rounded-xl bg-gray-100 p-4'
          : 'grid grid-cols-1 gap-2 sm:gap-3',
        !compact && TILE_COLUMNS[tiles.length],
        !compact && tiles.length === 1 && 'sm:hidden',
        className
      )}
    >
      {tiles.map((tile) => (
        <li
          key={tile.key}
          className={mergeClassNames(
            'flex items-center text-sm text-gray-700',
            !compact && 'gap-3 rounded-xl bg-gray-100 px-4 py-3',
            TILE_LAYOUT[layout],
            compact && tiles.length === 1 && 'justify-center'
          )}
        >
          <Illustration
            name={tile.illustration}
            alt=""
            width={40}
            height={40}
            className={mergeClassNames(
              'shrink-0 object-contain',
              compact ? 'h-6 w-6' : 'h-8 w-8 sm:h-10 sm:w-10',
              layout === 'pairs' && 'md:h-9 md:w-9'
            )}
          />
          <FactTileText tile={tile} twoLines={layout === 'rows'} />
        </li>
      ))}
    </ul>
  );
};
